import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import EmptyView from 'general/components/Others/EmptyView';
import AppColors from 'general/constants/AppColors';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AppRoute from 'general/constants/AppRoute';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import BreadcrumbHelper from 'general/components/CustomBreadcrumb/BreadcrumbHelper';
import AppConstants from 'general/constants/AppConstants';
import Utils from 'general/utils/Utils';
import KTBSProgressBar from 'general/components/OtherKeenComponents/KTBSProgressBar';
import PropTypes from 'prop-types';
import AgencyHelper from 'features/Agency/agencyHelper';

NewAgenciesListView.propTypes = {
    agencyRole: PropTypes.string,
    agencyId: PropTypes.string,
    newAgenciesList: PropTypes.array,
    newAgenciesTotal: PropTypes.number,
    filter: PropTypes.object,
    onFilterChanged: PropTypes.func,
};

const defaultColumnIds = ['AGENCY_CODE', 'AGENCY_NAME', 'AGENCY_LEVEL', 'AGENCY_TYPE', 'PHONE', 'EMAIL', 'TAXCODE/CCCD', 'STATUS'];

function NewAgenciesListView(props) {
    // ---- params ----
    const router = useRouter();
    const { t } = useTranslation();
    const { agencyRole, agencyId, newAgenciesList, newAgenciesTotal, onFilterChanged, filter } = props;
    const app = useSelector((state) => state.app);
    const { user } = useSelector((state) => state.auth);
    const [operatorList, setOperatorList] = useState([]);
    const fullColumns = useMemo(() => {
        return [
            {
                id: 'AGENCY_CODE',
                name: <span style={{ fontSize: 12, color: AppColors.gray[900] }} className="letter-spacing-unset">Mã {AgencyHelper.Utils.getShortAgencyRoleName(agencyRole)?.toLowerCase()}</span>,
                selector: (row) => row?.agencyCode ?? row.agencyId,
                minWidth: '140px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'AGENCY_NAME',
                name: <span style={{ fontSize: 12, color: AppColors.gray[900] }} className="letter-spacing-unset">Tên {AgencyHelper.Utils.getShortAgencyRoleName(agencyRole)?.toLowerCase()}</span>,
                selector: (row) => row.name ?? 'N/A',
                minWidth: '230px',
                style: tableStyle.columns.link,
            },
            {
                id: 'AGENCY_LEVEL',
                name: (
                    <span
                        className="letter-spacing-unset"
                        style={{
                            fontSize: 12,
                            color: AppColors.gray[900],
                        }}
                    >
                        Cấp {AgencyHelper.Utils.getShortAgencyRoleName(agencyRole)?.toLowerCase()}
                    </span>
                ),
                selector: (row) => {
                    return (
                        <span className="min-w-100px">
                            <KTLabel
                                text={DealerHelper.getAgencyLevel(row?.deepLevel)}
                                isInline={true}
                                size={KTLabelSize.medium}
                                additionalClassName={`label-light-primary`}
                            />
                        </span>
                    );
                },
                style: tableStyle.columns.normal,
                width: '140px',
            },
            {
                id: 'AGENCY_TYPE',
                name: <span style={{ fontSize: 12, color: AppColors.gray[900] }} className="letter-spacing-unset">
                    Loại {AgencyHelper.Utils.getShortAgencyRoleName(agencyRole)?.toLowerCase()}
                </span>,
                selector: (row) => row.type ? t(`accountType_${row.type}`) : 'N/A',
                style: tableStyle.columns.normal,
                minWidth: '140px',
            },
            {
                id: 'PHONE',
                name: (
                    <span
                        className="letter-spacing-unset"
                        style={{
                            fontSize: 12,
                            color: AppColors.gray[900],
                        }}
                    >
                        {t('phone')}
                    </span>
                ),
                selector: (row) => row.phone ?? '',
                style: tableStyle.columns.normal,
                width: '140px',
            },
            {
                id: 'EMAIL',
                name: (
                    <span
                        className="letter-spacing-unset"
                        style={{
                            fontSize: 12,
                            color: AppColors.gray[900],
                        }}
                    >
                        {t('email')}
                    </span>
                ),
                selector: (row) => {
                    return (
                        <KTTooltip offset={[0, 0]} text={row?.email ?? ''}>
                            <span>{row?.email ?? ''}</span>
                        </KTTooltip>
                    );
                },
                style: tableStyle.columns.normal,
                minWidth: '250px',
            },
            {
                id: 'TAXCODE/CCCD',
                name: (
                    <span
                        className="letter-spacing-unset"
                        style={{
                            fontSize: 12,
                            color: AppColors.gray[900],
                        }}
                    >
                        {t('MST/CCCD')}
                    </span>
                ),
                selector: (row) =>
                    row.type === AppConstants.accountType.individual
                        ? row.idCardNumber?.length > 0
                            ? row.idCardNumber
                            : row.taxCode?.length > 0
                                ? row.taxCode
                                : ''
                        : row.taxCode?.length > 0
                            ? row.taxCode
                            : '',
                style: tableStyle.columns.normal,
                width: '150px',
            },
            {
                id: 'REVENUE',
                name: (
                    <div className="w-100 d-flex flex-column align-items-end">
                        <div
                            className="letter-spacing-unset"
                            style={{
                                fontSize: 12,
                                color: AppColors.gray[900],
                            }}
                        >
                            {t('Doanh thu trong tháng')}
                        </div>
                        <div
                            className="letter-spacing-unset font-weight-bold"
                            style={{ color: AppColors.gray[700] }}
                        >
                            VNĐ
                        </div>
                    </div>
                ),
                selector: (row) => Utils.formatNumber(row?.revenue),
                width: '160px',
                right: true,
                style: tableStyle.columns.normal,
            },
            {
                id: 'KPI',
                name: <span style={{ fontSize: 12, color: AppColors.gray[900] }} className="letter-spacing-unset">{t('Tiến độ KPI')}</span>,
                selector: (row) => (
                    <div className='w-100'>
                        <KTBSProgressBar
                            completed={(row.saleKPI || row.packageKPI) ? Math.round((row.revenue / (row.saleKPI + row.packageKPI)) * 100) : 100}
                            value={row.revenue}
                            maxValue={row.saleKPI + row.packageKPI}
                            bgColor={AppColors.primary}
                            showValue={false}
                            width={120}
                        />
                    </div>
                ),
                style: tableStyle.columns.normal,
                width: '150px',
            },
            {
                id: 'STATUS',
                name: <span style={{ fontSize: 12, color: AppColors.gray[900] }} className="letter-spacing-unset">{t('status')}</span>,
                minWidth: '150px',
                selector: (row) => (
                    <KTLabel
                        text={t(`agencyStatus_${row.status}`)}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${DealerHelper.getAgencyStatusColor(row.status)}`}
                    />
                ),
            },
            {
                name: <span style={{ fontSize: 12, color: AppColors.gray[900] }} className="letter-spacing-unset">{t('actions')}</span>,
                width: '100px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                wrap: true,
                cell: (row) => (
                    <div className="d-flex gap-2 align-items-center">
                        <KTTooltip offset={[0, 0]} text={'Xem chi tiết'}>
                            <a className="btn btn-sm btn-clean btn-icon" onClick={() => handleClickedRow(row)}>
                                <i className="fa-regular fa-eye" style={{ color: AppColors.gray[700], fontSize: '1.1rem' }} />
                            </a>
                        </KTTooltip>
                    </div>
                ),
            },
        ];
    }, [t, operatorList]);

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = `${router.pathname}${router.location.hash}`;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(fullColumns?.map((row) => row?.id).filter(Boolean));
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        saveChanges,
        onSelectAll,
        onUnSelectAll,
        fullColumnsIdSorted
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

    // ---- methods ----

    const classifyOperators = (operators) => {
        setOperatorList(
            operators?.map((operator) => ({ operatorId: operator.operatorId, name: operator.name }))
        );
    };

    useEffect(() => {
        if (app.appConfig) {
            classifyOperators(app.appConfig.operators ?? []);
        }
    }, [app.appConfig]);

    const handleShowAddSubAgencyModel = () => {
        BreadcrumbHelper.setBreadcrumPath(t('addDealer'), `${router?.pathname}/add`);
        router.navigate('add');
    }

    const handleClickedRow = (row) => {
        Utils.openInNewTab(`${AppRoute.saleList}/${row.agencyId}`);
    };

    const handleFilterChanged = (filter) => {
        if (onFilterChanged) {
            onFilterChanged(filter);
        }
    };

    return (
        <div>
            {/* Body */}
            <div className="pt-4">
                <KTTable
                    columns={columns}
                    items={newAgenciesList}
                    selectableRows={false}
                    striped={false}
                    compactGrid={false}
                    onClickedRow={() => { }}
                    onSelectedRowsChanged={(rows) => { }}
                    onDoubleClickedRow={(rows) => { }}
                    noDataComponent={
                        <EmptyView
                            icon={imgEmptyNoData}
                            title="Không có dữ liệu để hiển thị"
                            text={
                                <div className="text-center">
                                    <span>
                                        Hiện không có dữ liệu để hiển thị. Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.
                                    </span>
                                </div>
                            }
                        />
                    }
                />
            </div>

            {/* Phân trang */}
            {!_.isEmpty(newAgenciesList) && (
                <div className="mt-4">
                    <KTPagination
                        currentPage={filter.page + 1}
                        rowCount={newAgenciesTotal}
                        rowsPerPage={filter.limit}
                        currentItemInPageCount={newAgenciesList.length}
                        onChangePage={(newPage) => {
                            const iNewPage = parseInt(newPage);
                            handleFilterChanged({
                                page: iNewPage - 1,
                            });
                        }}
                        onChangeRowsPerPage={(newRowsPerPage) => {
                            const iNewRowsPerPage = parseInt(newRowsPerPage);
                            handleFilterChanged({
                                limit: iNewRowsPerPage,
                                page: 0,
                            });
                        }}
                        loading={false}
                        color={KTPaginationColor.success}
                        appendText="đại lý"
                    />
                </div>
            )}
        </div>
    );
}

export default NewAgenciesListView;
